import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Badge,
  LinearProgress,
  Link
} from '@mui/material';
import {
  LinkOff as LinkOffIcon,
  Add as AddIcon,
  SkipNext as SkipIcon,
  Check as CheckIcon,
  Sync as SyncIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { piSyncApi, PIJobPreview, PIJobDecision, PIJobSyncResults, PICandidatePreview } from '../services/api';

interface PIJobSyncDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (results: PIJobSyncResults) => void;
}

interface JobAction {
  action: 'link' | 'create' | 'update' | 'unlink' | 'skip';
  localJobId?: number;
  candidateActions: Record<string, 'import' | 'skip'>;
}

interface ImportProgress {
  phase: 'jobs' | 'candidates';
  current: number;
  total: number;
  currentName?: string;
}

const PIJobSyncDialog: React.FC<PIJobSyncDialogProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const navigate = useNavigate();
  const [piJobs, setPiJobs] = useState<PIJobPreview[]>([]);
  const [localJobs, setLocalJobs] = useState<Array<{ id: number; title: string; department: string }>>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [actions, setActions] = useState<Record<string, JobAction>>({});
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleManualImport = () => {
    onClose();
    navigate('/candidates/import');
  };

  useEffect(() => {
    if (open) {
      fetchPreview();
    }
  }, [open]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await piSyncApi.previewJobs();
      setPiJobs(response.data.piJobs);
      setLocalJobs(response.data.localJobs);
      setTotalCandidates(response.data.totalCandidates);

      // Initialize default actions based on match status
      const defaultActions: Record<string, JobAction> = {};
      const autoExpand = new Set<string>();

      response.data.piJobs.forEach(job => {
        // Default candidate actions - import all non-linked candidates with complete assessments
        const candidateActions: Record<string, 'import' | 'skip'> = {};
        job.candidates.forEach(c => {
          if (c.matchStatus === 'linked') {
            candidateActions[c.piPersonId] = 'skip';
          } else if (c.assessmentComplete) {
            candidateActions[c.piPersonId] = 'import';
          } else {
            candidateActions[c.piPersonId] = 'skip';
          }
        });

        if (job.matchStatus === 'linked') {
          defaultActions[job.piJobId] = { action: 'update', candidateActions };
        } else if (job.matchStatus === 'suggested' && job.localJob) {
          defaultActions[job.piJobId] = { action: 'link', localJobId: job.localJob.id, candidateActions };
        } else if (job.candidateCount > 0) {
          // Jobs with candidates default to create
          defaultActions[job.piJobId] = { action: 'create', candidateActions };
        } else {
          defaultActions[job.piJobId] = { action: 'skip', candidateActions };
        }

        // Auto-expand jobs with candidates that will be imported
        if (job.candidateCount > 0 && defaultActions[job.piJobId].action !== 'skip') {
          autoExpand.add(job.piJobId);
        }
      });

      setActions(defaultActions);
      setExpandedJobs(autoExpand);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch PI jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobActionChange = (piJobId: string, action: 'link' | 'create' | 'update' | 'unlink' | 'skip', localJobId?: number) => {
    setActions(prev => ({
      ...prev,
      [piJobId]: { ...prev[piJobId], action, localJobId }
    }));

    // If skipping or unlinking, collapse the row
    if (action === 'skip' || action === 'unlink') {
      setExpandedJobs(prev => {
        const next = new Set(prev);
        next.delete(piJobId);
        return next;
      });
    }
  };

  const handleCandidateActionChange = (piJobId: string, piPersonId: string, action: 'import' | 'skip') => {
    setActions(prev => ({
      ...prev,
      [piJobId]: {
        ...prev[piJobId],
        candidateActions: {
          ...prev[piJobId].candidateActions,
          [piPersonId]: action
        }
      }
    }));
  };

  const toggleExpand = (piJobId: string) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(piJobId)) {
        next.delete(piJobId);
      } else {
        next.add(piJobId);
      }
      return next;
    });
  };

  const handleProcess = async () => {
    try {
      setProcessing(true);
      setError(null);
      setProgress({ phase: 'jobs', current: 0, total: 1 });

      // Build job decisions WITHOUT candidate decisions (we'll import candidates separately)
      const jobDecisions: PIJobDecision[] = Object.entries(actions).map(([piJobId, action]) => ({
        piJobId,
        action: action.action,
        localJobId: action.localJobId,
        candidates: [] // Empty - we'll import candidates one-by-one
      }));

      // Phase 1: Process jobs
      const jobResponse = await piSyncApi.processJobs(jobDecisions);
      const results: PIJobSyncResults = jobResponse.data.results;
      const jobIdMap = jobResponse.data.jobIdMap || {};

      // Build list of candidates to import with their job info
      interface CandidateToImport {
        piJobId: string;
        piPersonId: string;
        localJobId: number;
        name: string;
      }
      const candidatesToImport: CandidateToImport[] = [];

      for (const [piJobId, action] of Object.entries(actions)) {
        // Skip if job was skipped or unlinked
        if (action.action === 'skip' || action.action === 'unlink') continue;

        // Find the local job ID - first check jobIdMap (for newly created/linked jobs)
        const piJob = piJobs.find(j => j.piJobId === piJobId);
        let localJobId = jobIdMap[piJobId] || action.localJobId;

        // For updated jobs, get the localJobId from the piJob
        if (!localJobId && piJob?.localJob) {
          localJobId = piJob.localJob.id;
        }

        if (!localJobId) {
          console.warn(`No local job ID for PI job ${piJobId}, skipping candidates`);
          continue;
        }

        // Add candidates marked for import
        for (const [piPersonId, candAction] of Object.entries(action.candidateActions || {})) {
          if (candAction === 'import') {
            const candidate = piJob?.candidates.find(c => c.piPersonId === piPersonId);
            candidatesToImport.push({
              piJobId,
              piPersonId,
              localJobId,
              name: candidate ? `${candidate.firstName} ${candidate.lastName}` : piPersonId
            });
          }
        }
      }

      // Phase 2: Import candidates one-by-one
      if (candidatesToImport.length > 0) {
        setProgress({ phase: 'candidates', current: 0, total: candidatesToImport.length });

        for (let i = 0; i < candidatesToImport.length; i++) {
          const cand = candidatesToImport[i];
          setProgress({
            phase: 'candidates',
            current: i + 1,
            total: candidatesToImport.length,
            currentName: cand.name
          });

          try {
            const response = await piSyncApi.importSingleCandidate({
              piJobId: cand.piJobId,
              piPersonId: cand.piPersonId,
              localJobId: cand.localJobId
            });

            if (response.data.status === 'imported') {
              results.candidates.imported++;
            } else if (response.data.status === 'already_exists') {
              results.candidates.skipped++;
            }
          } catch (err: any) {
            results.candidates.errors.push({
              piPersonId: cand.piPersonId,
              error: err.response?.data?.error || err.message
            });
          }
        }
      }

      setProgress(null);
      onComplete(results);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process import');
      setProgress(null);
    } finally {
      setProcessing(false);
    }
  };

  const formatTargetRange = (job: PIJobPreview) => {
    if (!job.targetFactorRanges) return 'No targets';
    const { aSigmaRangeLow: aLow, aSigmaRangeHigh: aHigh, bSigmaRangeLow: bLow, bSigmaRangeHigh: bHigh } = job.targetFactorRanges;
    if (aLow === undefined && bLow === undefined) return 'No targets';
    return `A: ${aLow?.toFixed(1) || '?'}/${aHigh?.toFixed(1) || '?'}, B: ${bLow?.toFixed(1) || '?'}/${bHigh?.toFixed(1) || '?'}...`;
  };

  const getJobStatusChip = (job: PIJobPreview) => {
    switch (job.matchStatus) {
      case 'linked':
        return <Chip size="small" color="success" icon={<CheckIcon />} label={`Linked: ${job.localJob?.title}`} />;
      case 'suggested':
        return <Chip size="small" color="warning" icon={<HelpIcon />} label={`Suggested: ${job.localJob?.title}`} />;
      default:
        return <Chip size="small" color="default" label="No match" />;
    }
  };

  const getCandidateStatusChip = (candidate: PICandidatePreview) => {
    if (candidate.matchStatus === 'linked') {
      return <Chip size="small" color="success" label="Already imported" />;
    }
    if (!candidate.assessmentComplete) {
      return <Chip size="small" color="warning" icon={<WarningIcon />} label="Pending" />;
    }
    return <Chip size="small" color="info" label="Complete" />;
  };

  const getSummary = () => {
    const jobSummary = { link: 0, create: 0, update: 0, unlink: 0, skip: 0 };
    let candidatesToImport = 0;

    Object.entries(actions).forEach(([piJobId, action]) => {
      jobSummary[action.action]++;

      // Only count candidates if job is not being skipped/unlinked
      if (action.action !== 'skip' && action.action !== 'unlink') {
        Object.values(action.candidateActions || {}).forEach(candAction => {
          if (candAction === 'import') {
            candidatesToImport++;
          }
        });
      }
    });

    return { jobs: jobSummary, candidatesToImport };
  };

  const summary = getSummary();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SyncIcon color="primary" />
          <Typography variant="h6">Import from PI</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : piJobs.length === 0 ? (
          <Alert severity="info">No jobs found in PI</Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {piJobs.length} jobs in PI with {totalCandidates} total candidates.
            </Alert>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}></TableCell>
                    <TableCell><strong>PI Job</strong></TableCell>
                    <TableCell><strong>Targets</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {piJobs.map(job => {
                    const currentAction = actions[job.piJobId];
                    const isExpanded = expandedJobs.has(job.piJobId);
                    const hasCandidates = job.candidateCount > 0;
                    const candidatesToImport = Object.values(currentAction?.candidateActions || {}).filter(a => a === 'import').length;

                    return (
                      <React.Fragment key={job.piJobId}>
                        <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}>
                          <TableCell>
                            {hasCandidates && (
                              <IconButton size="small" onClick={() => toggleExpand(job.piJobId)}>
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight="medium">
                                {job.name}
                              </Typography>
                              {hasCandidates && (
                                <Badge badgeContent={candidatesToImport > 0 ? candidatesToImport : job.candidateCount} color={candidatesToImport > 0 ? "primary" : "default"}>
                                  <PersonIcon fontSize="small" color="action" />
                                </Badge>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatTargetRange(job)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {getJobStatusChip(job)}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {job.matchStatus === 'linked' ? (
                                <>
                                  <Tooltip title="Update targets from PI">
                                    <IconButton
                                      size="small"
                                      color={currentAction?.action === 'update' ? 'primary' : 'default'}
                                      onClick={() => handleJobActionChange(job.piJobId, 'update')}
                                    >
                                      <SyncIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Unlink from local job">
                                    <IconButton
                                      size="small"
                                      color={currentAction?.action === 'unlink' ? 'error' : 'default'}
                                      onClick={() => handleJobActionChange(job.piJobId, 'unlink')}
                                    >
                                      <LinkOffIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Skip">
                                    <IconButton
                                      size="small"
                                      color={currentAction?.action === 'skip' ? 'primary' : 'default'}
                                      onClick={() => handleJobActionChange(job.piJobId, 'skip')}
                                    >
                                      <SkipIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select
                                      value={currentAction?.action === 'link' ? currentAction.localJobId || '' : ''}
                                      displayEmpty
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleJobActionChange(job.piJobId, 'link', Number(e.target.value));
                                        }
                                      }}
                                      renderValue={(value) => {
                                        if (!value) return <em>Link to...</em>;
                                        const linkedJob = localJobs.find(j => j.id === value);
                                        return linkedJob?.title || 'Link to...';
                                      }}
                                    >
                                      <MenuItem value="" disabled><em>Link to existing job</em></MenuItem>
                                      {localJobs.map(localJob => (
                                        <MenuItem key={localJob.id} value={localJob.id}>
                                          {localJob.title} ({localJob.department})
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Tooltip title="Create new job">
                                    <IconButton
                                      size="small"
                                      color={currentAction?.action === 'create' ? 'primary' : 'default'}
                                      onClick={() => handleJobActionChange(job.piJobId, 'create')}
                                    >
                                      <AddIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Skip">
                                    <IconButton
                                      size="small"
                                      color={currentAction?.action === 'skip' ? 'primary' : 'default'}
                                      onClick={() => handleJobActionChange(job.piJobId, 'skip')}
                                    >
                                      <SkipIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expandable candidate rows */}
                        {hasCandidates && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0, bgcolor: 'grey.50' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 1, px: 2, pl: 6 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Candidates ({job.candidateCount})
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Assessment</strong></TableCell>
                                        <TableCell><strong>Action</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {job.candidates.map(candidate => {
                                        const candAction = currentAction?.candidateActions?.[candidate.piPersonId] || 'skip';
                                        const isLinked = candidate.matchStatus === 'linked';

                                        return (
                                          <TableRow key={candidate.piPersonId}>
                                            <TableCell>
                                              {candidate.firstName} {candidate.lastName}
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" color="text.secondary">
                                                {candidate.email || 'No email'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              {getCandidateStatusChip(candidate)}
                                            </TableCell>
                                            <TableCell>
                                              {isLinked ? (
                                                <Chip size="small" label="Already imported" />
                                              ) : (
                                                <Box display="flex" gap={0.5}>
                                                  <Tooltip title="Import candidate">
                                                    <IconButton
                                                      size="small"
                                                      color={candAction === 'import' ? 'primary' : 'default'}
                                                      onClick={() => handleCandidateActionChange(job.piJobId, candidate.piPersonId, 'import')}
                                                      disabled={currentAction?.action === 'skip' || currentAction?.action === 'unlink'}
                                                    >
                                                      <AddIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Tooltip title="Skip">
                                                    <IconButton
                                                      size="small"
                                                      color={candAction === 'skip' ? 'primary' : 'default'}
                                                      onClick={() => handleCandidateActionChange(job.piJobId, candidate.piPersonId, 'skip')}
                                                      disabled={currentAction?.action === 'skip' || currentAction?.action === 'unlink'}
                                                    >
                                                      <SkipIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ mr: 1 }}>Jobs:</Typography>
                {summary.jobs.update > 0 && (
                  <Chip label={`${summary.jobs.update} update`} color="primary" variant="outlined" size="small" />
                )}
                {summary.jobs.link > 0 && (
                  <Chip label={`${summary.jobs.link} link`} color="success" variant="outlined" size="small" />
                )}
                {summary.jobs.create > 0 && (
                  <Chip label={`${summary.jobs.create} create`} color="info" variant="outlined" size="small" />
                )}
                {summary.jobs.unlink > 0 && (
                  <Chip label={`${summary.jobs.unlink} unlink`} color="error" variant="outlined" size="small" />
                )}
                {summary.jobs.skip > 0 && (
                  <Chip label={`${summary.jobs.skip} skip`} color="default" variant="outlined" size="small" />
                )}
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="subtitle2">Candidates:</Typography>
                <Chip
                  label={`${summary.candidatesToImport} to import`}
                  color={summary.candidatesToImport > 0 ? 'primary' : 'default'}
                  size="small"
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
        {progress && (
          <Box sx={{ width: '100%', mb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {progress.phase === 'jobs'
                  ? 'Processing jobs...'
                  : `Importing candidate ${progress.current} of ${progress.total}`}
              </Typography>
              {progress.phase === 'candidates' && (
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {progress.currentName}
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.phase === 'jobs' ? 0 : (progress.current / progress.total) * 100}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Link
            component="button"
            variant="body2"
            color="text.secondary"
            onClick={handleManualImport}
            disabled={processing}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Manual import (paste HTML)
          </Link>
          <Box display="flex" gap={1}>
            <Button onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleProcess}
              disabled={loading || processing || piJobs.length === 0}
              startIcon={processing ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              {processing
                ? (progress?.phase === 'jobs' ? 'Processing jobs...' : `Importing ${progress?.current || 0}/${progress?.total || 0}...`)
                : 'Import Jobs & Candidates'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PIJobSyncDialog;
